import { RuleValueFormat, ValueSource } from "constants/ruleBooks";

import { useRuleFieldSchema } from "containers/Settings/RuleBooks/Schema/schemaContext";
import { FieldValueType, ValueSourceExt } from "./constants";
import { RoundingMethod } from "./lib";
import {
  getDataTypeByPath,
  getPathParts,
  getRelationPath,
  getType,
  LinkedDataType,
  LinkedRootDataType,
  normalisePathParts,
} from "./Schema/schema";
import { SchemaFieldDataSourceField } from "./DataSource/SchemaFieldDataSource";

export function convertToSchemaPath(absJsonPath: string): string | null {
  if (!absJsonPath) {
    return null;
  }
  const path = absJsonPath
    // replace the bracket notation is with dot notation
    // e.g. $["lots"]["stock_agents"] becomes $.lots.stock_agents
    .replace(/\["([^"]+)"\]/g, ".$1")

    // remove the leading $.
    .replace(/^\$\./, "")

    // and trailing . if present
    .replace(/\.$/, "");

  return normalisePathParts(getPathParts(path).map(getRelationPath));
}
export function getParentInputDataType(
  inputPathParts: string[],
  schema: LinkedRootDataType,
): LinkedDataType | null {
  if (!inputPathParts || inputPathParts.length === 0) {
    return null;
  }
  const path = convertToSchemaPath(normalisePathParts(inputPathParts));
  return getDataTypeByPath(schema, path);
}

function getDataSourcePath(dataSource) {
  const dataSourcePath = [dataSource.value, dataSource.key]
    .filter(Boolean)
    .map(getRelationPath)
    .join(".");
  return dataSourcePath;
}

function convertToInternalDataSourceField(
  dataSource,
  parentDataType,
  defaultValueType,
) {
  let valueType = dataSource.value_type || defaultValueType;
  if (
    [ValueSource.FIELD, ValueSource.RULESET_FIELD].includes(
      dataSource.source,
    ) &&
    dataSource.value_type === null
  ) {
    valueType = getType(parentDataType, dataSource.value) || defaultValueType;
  }
  return {
    source: dataSource.source,
    value:
      typeof dataSource.value === "string"
        ? getDataSourcePath(dataSource)
        : dataSource.value,
    criteria_output_index: dataSource.criteria_output_index,
    valueType,
  };
}

function convertToInternalDataSource(
  dataSource,
  parentDataType,
  criteriaDataTypes = [],
) {
  if (dataSource === undefined) {
    return dataSource;
  }
  if (
    [
      ValueSource.CONSTANT,
      ValueSource.COUNT,
      ValueSource.FIELD,
      ValueSource.RULESET_FIELD,
      ValueSource.DEPENDENCY_VALUE,
      ValueSource.GLOBAL_BUSINESS,
    ].includes(dataSource.source)
  ) {
    const defaultValueType = {
      [ValueSource.CONSTANT]: null,
      [ValueSource.COUNT]: FieldValueType.INTEGER,
      [ValueSource.FIELD]: null,
      [ValueSource.GLOBAL_BUSINESS]: FieldValueType.BUSINESS,
    }[dataSource.source];
    return convertToInternalDataSourceField(
      dataSource,
      parentDataType,
      defaultValueType,
    );
  }

  if (dataSource.source === ValueSourceExt.SUM) {
    return convertToInternalDataSourceField(
      dataSource,
      parentDataType,
      getType(
        criteriaDataTypes[dataSource.criteria_output_index],
        dataSource.value,
      ),
    );
  }
  // eslint-disable-next-line no-console
  console.warn(
    `Unknown data source type "${dataSource.source}", passing through`,
  );
  return dataSource;
}

export function convertToInternalCheck(
  check: Check | LegacyCheck,
  parentDataType: LinkedDataType | null,
): Check {
  const {
    value: externalValue,
    values: externalValues,
    ...externalCheck
  } = check as LegacyCheck & Check;
  const internalCheck = { ...externalCheck } as Check;
  if (externalValue !== undefined && !Array.isArray(externalValues)) {
    // Migrate a v1 Rule Book `value` attr to `values`
    internalCheck.values = [externalValue];
  } else if (Array.isArray(externalValues) && externalValues.length > 0) {
    internalCheck.values = externalValues;
  } else {
    // eslint-disable-next-line no-console
    console.warn(
      "Invalid Check schema: `value` was undefined and `values` was not an array, or was empty. Defaulting to an empty array",
      check,
    );
    internalCheck.values = [""];
  }

  internalCheck.field = convertToInternalDataSource(
    internalCheck.field,
    parentDataType,
  );

  return internalCheck;
}

export function convertToInternalRelatedDataQuery(
  relatedDataQuery,
): SchemaFieldDataSourceField {
  if (!relatedDataQuery) {
    return null;
  }

  let newPath = convertToSchemaPath(relatedDataQuery.path);
  if (relatedDataQuery.path.startsWith("$")) {
    // When the path starts with `$` it is an old style, convert it to a straight JSON path
    newPath = `${convertToSchemaPath(relatedDataQuery.query_field)}_${convertToSchemaPath(relatedDataQuery.path)}`;
  }
  return convertToInternalDataSourceField(
    { source: ValueSource.FIELD, value: newPath },
    null,
    null,
  );
}

function migrateChecks(checks) {
  if (checks.length > 0) {
    if (Array.isArray(checks[0])) {
      // Checks are already migrated
      return checks;
    }
    // migrate checks into check groups
    return [checks];
  }
  return [];
}

export function convertToInternalCriteria(criteria, dataType) {
  const { checks, ...internalCriteria } = criteria;

  internalCriteria.query = internalCriteria.query
    ? convertToInternalRelatedDataQuery(internalCriteria.query)
    : null;

  const checkParentDataType =
    getDataTypeByPath(dataType, internalCriteria.query?.value) || dataType;

  const checkGroups = migrateChecks(checks);

  internalCriteria.checkGroups = checkGroups.map(checkGroup =>
    checkGroup.map(check => convertToInternalCheck(check, checkParentDataType)),
  );

  return internalCriteria;
}

export function convertToInternalValue(
  ruleValue,
  parentDataType,
  criteriaDataTypes,
) {
  const internalRuleValue = {
    amount: convertToInternalDataSource(
      ruleValue.amount,
      parentDataType,
      criteriaDataTypes,
    ),
    units: convertToInternalDataSource(
      ruleValue.units,
      parentDataType,
      criteriaDataTypes,
    ),
    offset: undefined,
    unit_offset: undefined,
    unit_limit: undefined,
    rounding_strategy: undefined,
    rounding_value: undefined,
    output_as_single_unit: undefined,
    apply_splits: undefined,
    apply_criteria_filter_to_splits: undefined,
  };

  if (ruleValue.offset) {
    // `offset` was originally the unit price offset, which was deprecated 03/2022.
    // This has been superseded by the combination of `offset` (total price offset) and
    // `unit_offset`, which results in a `calculated_units` after first applying the `unit_limit`.
    internalRuleValue.offset = convertToInternalDataSource(
      ruleValue.offset,
      parentDataType,
      criteriaDataTypes,
    );
  } else {
    internalRuleValue.offset = convertToInternalDataSourceField(
      { source: ValueSource.CONSTANT, value: 0 },
      parentDataType,
      FieldValueType.INTEGER,
    );
  }

  if (ruleValue.unit_offset) {
    // `unit_offset` was introduced as part of migrating away from having a _calculated_, _clamped_
    // and _offset_ unit price in favor of applying clamping, and offset to the unit count instead and
    // provided a total price offset in 03/2022
    internalRuleValue.unit_offset = convertToInternalDataSource(
      ruleValue.unit_offset,
      parentDataType,
      criteriaDataTypes,
    );
  } else {
    internalRuleValue.unit_offset = convertToInternalDataSourceField(
      { source: ValueSource.CONSTANT, value: 0 },
      parentDataType,
      FieldValueType.INTEGER,
    );
  }

  if (ruleValue.unit_limit) {
    // `limit`, which was originally a unit price limit, was deprecated 03/2022 in favour of a unit count limit.
    internalRuleValue.unit_limit = convertToInternalDataSource(
      ruleValue.unit_limit,
      parentDataType,
      criteriaDataTypes,
    );
  } else {
    internalRuleValue.unit_limit = convertToInternalDataSourceField(
      { source: ValueSource.CONSTANT, value: 0 },
      parentDataType,
      FieldValueType.INTEGER,
    );
  }
  internalRuleValue.rounding_strategy =
    ruleValue.rounding_strategy === undefined
      ? RoundingMethod.NONE
      : ruleValue.rounding_strategy;

  internalRuleValue.rounding_value =
    ruleValue.rounding_value === undefined ? 1 : ruleValue.rounding_value;
  internalRuleValue.output_as_single_unit =
    ruleValue.output_as_single_unit === undefined
      ? false
      : ruleValue.output_as_single_unit;

  internalRuleValue.apply_splits =
    ruleValue.apply_splits === undefined ? true : ruleValue.apply_splits;

  internalRuleValue.apply_criteria_filter_to_splits =
    ruleValue.apply_criteria_filter_to_splits === undefined
      ? true
      : ruleValue.apply_criteria_filter_to_splits;

  return internalRuleValue;
}

function convertToInternalFee(fee, parentDataType, criteriaDataTypes) {
  const internalFee = {
    apportionFromSource: fee.apportion_from_source,
    enabledQuery: convertToInternalDataSource(
      fee.enabled_query,
      parentDataType,
      criteriaDataTypes,
    ),
    name: fee.name,
    operation: fee.operation,
    operationInput: convertToInternalDataSource(
      fee.operation_input,
      parentDataType,
      criteriaDataTypes,
    ),
    sourceIndex: undefined,
  };

  if (typeof fee.source_index === "number") {
    internalFee.sourceIndex = fee.source_index;
  }

  return internalFee;
}

export function convertToInternalRuleSet(
  ruleSet: RuleSet,
  schema: ReturnType<typeof useRuleFieldSchema>,
): InternalRuleSet {
  const baseDataType = schema;
  const inputFieldPath = convertToSchemaPath(ruleSet.input_field);

  const rootDataType = inputFieldPath
    ? getDataTypeByPath(baseDataType, inputFieldPath)
    : baseDataType;

  const inputField = inputFieldPath
    ? convertToInternalDataSourceField(
        {
          source: ValueSource.FIELD,
          value: inputFieldPath,
        },
        rootDataType,
        null,
      )
    : null;

  const criteriaDataTypes = [];

  const criteria =
    ruleSet.criteria?.map(externalCriteria => {
      const internalCriteria = convertToInternalCriteria(
        externalCriteria,
        rootDataType,
      );
      const criteriaDataType = internalCriteria.query?.value
        ? getDataTypeByPath(rootDataType, internalCriteria.query.value)
        : rootDataType;

      criteriaDataTypes.push(criteriaDataType);
      return internalCriteria;
    }) || [];

  const internalRuleSet: InternalRuleSet = {
    ...ruleSet,
    input_field: inputField,
    criteria,
  };

  return internalRuleSet;
}

export function convertToInternalRule(
  rule: Rule,
  schema: LinkedRootDataType,
  parentDataType: LinkedDataType | null,
) {
  const { content, ...remainingRule } = rule;
  const ruleContent = JSON.parse(content || "null");
  const internalRule = {
    ...ruleContent,
    ...remainingRule,
  };

  const baseDataType = parentDataType || schema;

  const inputFieldPath = convertToSchemaPath(internalRule.input_field);
  const rootDataType = inputFieldPath
    ? getDataTypeByPath(baseDataType, inputFieldPath)
    : baseDataType;

  internalRule.input_field = inputFieldPath
    ? convertToInternalDataSourceField(
        {
          source: ValueSource.FIELD,
          value: inputFieldPath,
        },
        rootDataType,
        null,
      )
    : null;

  const criteriaDataTypes = [];

  internalRule.criteria = internalRule.criteria.map(externalCriteria => {
    const internalCriteria = convertToInternalCriteria(
      externalCriteria,
      rootDataType,
    );
    const criteriaDataType = internalCriteria.query?.value
      ? getDataTypeByPath(rootDataType, internalCriteria.query.value)
      : rootDataType;

    criteriaDataTypes.push(criteriaDataType);
    return internalCriteria;
  });

  internalRule.from_business = convertToInternalDataSource(
    ruleContent.from_business,
    rootDataType,
  );
  internalRule.to_business = convertToInternalDataSource(
    ruleContent.to_business,
    rootDataType,
  );

  internalRule.value = convertToInternalValue(
    ruleContent.value,
    rootDataType,
    criteriaDataTypes,
  );

  const fees = Array.isArray(ruleContent.fees) ? ruleContent.fees : [];

  internalRule.fees = fees.map(fee =>
    convertToInternalFee(fee, rootDataType, criteriaDataTypes),
  );

  // Migrate older Rules to have a Category
  internalRule.category = ruleContent.category || "";
  internalRule.billing_tags = ruleContent.billing_tags || [];

  internalRule.quantity_output_format =
    internalRule.quantity_output_format || RuleValueFormat.UNITS;

  internalRule.quantity_raw_format =
    internalRule.quantity_raw_format || RuleValueFormat.UNITS;

  internalRule.unit_amount_output_format =
    internalRule.unit_amount_output_format || RuleValueFormat.DOLLARS;

  internalRule.unit_amount_raw_format =
    internalRule.unit_amount_raw_format || RuleValueFormat.CENTS;

  // Multiplier allows multiplication of the output - multiplying by 1 has 0 effect, so migrate old rules to that.
  internalRule.price_multiplier =
    ruleContent.price_multiplier === undefined
      ? 1
      : ruleContent.price_multiplier;

  return internalRule;
}
